import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCan } from "@/lib/authz";
import { UploadSimple, DownloadSimple, CheckCircle, Warning } from "@phosphor-icons/react";
import BulkImporter from "@/components/BulkImporter";

// Legacy single-shot tabs (candidates + employees) — kept for backwards-compat
// with the Phase 6E importer.
const LEGACY_TABS = [
  { key: "candidates", label: "Candidate CSV", template: "/imports/candidates/template",
    preview: "/imports/candidates/preview", commit: "/imports/candidates/commit",
    perm: "candidate.create" },
  { key: "employees", label: "Employee CSV", template: "/imports/employees/template",
    preview: "/imports/employees/preview", commit: "/imports/employees/commit",
    perm: "employee.create" },
];

// Phase 11 Beat 2 — bulk Excel imports (existing candidate migration).
const BULK_TABS = [
  { key: "bulk-candidate-info", label: "Bulk Candidate Info", perm: "candidate.edit",
    templatePath: "/imports/candidate-info/template",
    previewPath: "/imports/candidate-info/preview",
    commitPath: "/imports/candidate-info/commit",
    templateName: "candidate_info_template.xlsx",
    title: "Bulk Candidate Information Import",
    description: "Migrate personal, contact, address, education, experience, skills, visa, resume, LinkedIn, marketing, and service information for existing candidates. Duplicates are merged — never re-created." },
  { key: "bulk-evaluations", label: "Bulk Evaluations", perm: "candidate.edit",
    templatePath: "/imports/evaluations/template",
    previewPath: "/imports/evaluations/preview",
    commitPath: "/imports/evaluations/commit",
    templateName: "evaluation_import_template.xlsx",
    title: "Bulk Evaluation Import",
    description: "Import historical evaluations — one row per evaluation. Preserves date, evaluator, remarks, version, and marketing-ready flag. Creates immutable evaluation history and syncs the candidate profile." },
];

export default function Imports() {
  // Phase D — hooks must be called unconditionally at top of component.
  const canCreateCandidate = useCan("candidate.create");
  const canEditCandidate = useCan("candidate.edit");
  const canCreateEmployee = useCan("employee.create");
  const canEditEmployee = useCan("employee.edit");
  const canImportCandidates = canCreateCandidate || canEditCandidate;
  const canImportEmployees = canCreateEmployee || canEditEmployee;

  const visibleLegacy = LEGACY_TABS.filter((t) =>
    (t.key === "candidates" && canImportCandidates) ||
    (t.key === "employees" && canImportEmployees)
  );
  const visibleBulk = canImportCandidates ? BULK_TABS : [];
  const allTabs = [...visibleBulk, ...visibleLegacy];

  const [tab, setTab] = useState(allTabs[0]?.key || "");
  const activeTab = allTabs.find((t) => t.key === tab) || allTabs[0];

  // Legacy CSV importer state
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  if (allTabs.length === 0) {
    return (
      <div className="max-w-2xl mx-auto p-6" data-testid="imports-denied">
        <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
          <div className="font-semibold text-amber-900">Access restricted</div>
          <p className="text-sm text-amber-800 mt-1">You do not have permission to import Candidates or Employees. Ask an administrator to grant <code>candidate.create</code>, <code>candidate.edit</code>, or <code>employee.create</code> to your role.</p>
        </div>
      </div>
    );
  }

  const isBulk = activeTab && activeTab.templatePath;

  const downloadTemplate = async () => {
    if (!activeTab) return;
    const r = await api.get(activeTab.template, { responseType: "blob" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(new Blob([r.data]));
    link.download = `${activeTab.key}_template.csv`; link.click();
  };

  const doPreview = async () => {
    if (!file) { toast.error("Select a CSV file first"); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post(activeTab.preview, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Preview failed"); }
    finally { setBusy(false); }
  };

  const doCommit = async () => {
    if (!file || !preview) return;
    if (preview.errors?.length) { toast.error("Fix errors before committing"); return; }
    if (!window.confirm(`Import ${preview.valid} rows? Nothing is inserted if any row fails.`)) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post(activeTab.commit, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (r.data.ok) { toast.success(`Imported ${r.data.inserted} rows`); setFile(null); setPreview(null); }
      else toast.error(r.data.message || "Import failed");
    } catch (e) { toast.error(e.response?.data?.detail || "Commit failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="imports-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-400">Administration</div>
        <h2 className="text-2xl font-semibold">Data Import</h2>
        <p className="text-sm text-slate-500">Migrate existing candidates + evaluations via Excel, or use legacy CSV importers for new candidates/employees.</p>
      </div>

      <div className="flex gap-2 border-b border-slate-200 overflow-x-auto">
        {allTabs.map((t) => (
          <button key={t.key} onClick={() => { setTab(t.key); setFile(null); setPreview(null); }}
                  data-testid={`import-tab-${t.key}`}
                  className={`px-3 py-2 text-sm border-b-2 whitespace-nowrap transition ${tab === t.key ? "border-slate-900 font-medium text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {isBulk ? (
        <BulkImporter
          templatePath={activeTab.templatePath}
          previewPath={activeTab.previewPath}
          commitPath={activeTab.commitPath}
          templateName={activeTab.templateName}
          title={activeTab.title}
          description={activeTab.description}
          testidBase={activeTab.key}
        />
      ) : (
        <div className="bg-white border border-slate-200 rounded-md p-4 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={downloadTemplate} data-testid="download-template"
                    className="text-sm border border-slate-200 rounded-md px-3 py-2 inline-flex items-center gap-1 hover:bg-slate-50">
              <DownloadSimple size={14} /> Download CSV template
            </button>
            <input type="file" accept=".csv,.xlsx" onChange={(e) => { setFile(e.target.files?.[0]); setPreview(null); }}
                   data-testid="import-file"
                   className="text-sm border border-slate-200 rounded-md px-3 py-2" />
            <button onClick={doPreview} disabled={busy || !file} data-testid="import-preview-btn"
                    className="text-sm bg-sky-600 hover:bg-sky-700 disabled:opacity-50 text-white rounded-md px-3 py-2">
              Preview & validate
            </button>
            <button onClick={doCommit} disabled={busy || !preview || preview.errors?.length}
                    data-testid="import-commit-btn"
                    className="text-sm bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-md px-3 py-2 inline-flex items-center gap-1">
              <UploadSimple size={14} /> Commit import
            </button>
          </div>

          {preview && (
            <div className="space-y-3" data-testid="import-preview">
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div className="bg-slate-50 rounded-md p-3">
                  <div className="text-xs text-slate-500">Total rows</div>
                  <div className="text-2xl font-semibold">{preview.total_rows}</div>
                </div>
                <div className={`rounded-md p-3 ${preview.valid > 0 ? "bg-emerald-50 text-emerald-800" : "bg-slate-50"}`}>
                  <div className="text-xs flex items-center gap-1"><CheckCircle size={12} /> Valid</div>
                  <div className="text-2xl font-semibold">{preview.valid}</div>
                </div>
                <div className={`rounded-md p-3 ${preview.errors?.length ? "bg-red-50 text-red-800" : "bg-slate-50"}`}>
                  <div className="text-xs flex items-center gap-1"><Warning size={12} /> Errors</div>
                  <div className="text-2xl font-semibold">{preview.errors?.length || 0}</div>
                </div>
              </div>
              {preview.errors?.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3" data-testid="import-errors">
                  <div className="text-sm font-semibold mb-2 text-red-800">Errors — fix these lines in your file and re-preview:</div>
                  <ul className="text-xs space-y-1 max-h-60 overflow-y-auto">
                    {preview.errors.map((e, i) => (
                      <li key={`${e.row}-${e.email || i}`} className="text-red-700">Row {e.row} · {e.email || "?"} — {e.errors.join("; ")}</li>
                    ))}
                  </ul>
                </div>
              )}
              {preview.preview?.length > 0 && (
                <div className="text-xs">
                  <div className="text-slate-500 mb-1">Preview (first {preview.preview.length} valid rows):</div>
                  <pre className="bg-slate-50 border border-slate-200 rounded-md p-2 overflow-x-auto max-h-60">{JSON.stringify(preview.preview, null, 2)}</pre>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
